import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import DefaultConfig from "./defaultConfig";
import {getTenantName} from "./index";
import { isAuthenticated, getAuthType, getAuthToken, getIdToken, getGroupsId } from "../authentication/Authorizer";
import { getAccountSessionToken } from "../authentication/accountSession";
import { i18n } from "../i18n/i18n";

const BASE_URL = process.env.REACT_APP_API_BASE_URL as string;
const NEW_BASE_URL = process.env.REACT_APP_NEW_API_BASE_URL as string;

export const axiosNewPrivate = axios.create({
    baseURL: NEW_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': DefaultConfig.api.key,
        'X-Language': 'de',
        'X-Tenant': getTenantName()
    },
});

// H-3a account session (OTP login): inject the bearer token on the NEW backend
// only — the legacy backend never sees account sessions. The token lives in
// authentication/accountSession.ts, the single swap point if session transport
// moves to cookies (issue #1679 Q2).
axiosNewPrivate.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const accountSessionToken = getAccountSessionToken();
        if (accountSessionToken && config.headers) {
            config.headers['Authorization'] = `Bearer ${accountSessionToken}`;
        }
        return config;
    },
    (error: AxiosError) => Promise.reject(error),
);

const axiosInstance = axios.create({
    baseURL: BASE_URL,
});

export default axiosInstance;

// Axios instance for private API requests with JSON headers
export const axiosPrivate = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': DefaultConfig.api.key,
        'X-Language': 'de',
        'X-Tenant': getTenantName()
    },
});

// Request interceptor for axiosPrivate to add the Authorization and X-Tenant headers
axiosPrivate.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        // Add language header
        if (config.headers) {
            config.headers['X-Language'] = i18n.language;
        }

        // H-3a (#1874): the legacy backend creates the orders, so it has to be
        // able to establish for itself which account a booking belongs to —
        // it resolves this token against account_session (never a client-sent
        // account id). A dedicated header rather than `Authorization`, which
        // the rentalAuth API Gateway authorizer already owns on the door and
        // damage-report endpoints.
        const accountSessionToken = getAccountSessionToken();
        if (accountSessionToken && config.headers) {
            config.headers['X-Account-Session'] = accountSessionToken;
        }

        const isLogged = await isAuthenticated();
        
        // Add auth token and authtype when the user is logged in
        if (isLogged === true && config.headers) {
            const authType = await getAuthType();
            const authToken = await getAuthToken(authType);

            if (authToken) {
                config.headers['X-Auth-Token'] = authToken;
                config.headers['X-Auth-Type'] = authType;
            }
            
            if (authType === "entraid") {
                const idToken = getIdToken();
                if (idToken) {
                    const groupsId = getGroupsId(idToken);
                    if (groupsId && groupsId.length > 0) {
                        // Here we suppose a user can be within only one group (we retrieve first group then)
                        config.headers['X-Group-Id'] = groupsId[0];
                    }
                }
            }
        }

        return config;
    },
    (error: AxiosError) => Promise.reject(error),
);